import { useRef, useState } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { useCombobox } from 'downshift'
import { type FieldPathValue, type FieldValues, type Path, useController, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useUnique } from '@subaio/components'
import { type CommonSelectProps, useAsyncOptions } from '@subaio/components/src/HookForm'

import { ColumnLabel, InputError, Label, MenuIndicator, SelectMenu } from '../Shared'
import { InlineValue } from './InlineValue'
import { InputComponent } from './InputComponent'
import { InputContainer } from './InputContainer'
import { LoadingLabel } from './LoadingLabel'
import { ValueWrapper } from './ValueWrapper'

const defaultValue: any = ''

/**
 * A select component that allows searching for and selecting a single value.
 * Default return value is `undefined`
 * @example
 * ```tsx
 * <Select name='name' label='Select' options={[{ label: 'Option', value: 'option-1' }, ...]} />
 * ```
 */
export const Select = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	isRequired = false,
	maxNumberOfOptionsVisible = 40,
	isReadOnly = false,
	isDisabled = false,
	name,
	label,
	hint,
	placeholder,
	options: providedOptions,
	shouldAutofocus = false,
	isLoading = false,
	validate = {},
	...rest
}: CommonSelectProps<TPath, FieldPathValue<TFieldValues, TPath>>) => {
	// Input settings and setup
	const id = useUnique(name)
	const { getValues } = useForm()
	const { formatMessage } = useIntl()

	const {
		field: { value, onChange },
		fieldState: { error },
	} = useController<TFieldValues, any>({
		name,
		disabled: isDisabled,
		rules: {
			validate: (value) => {
				Object.values(validate).forEach((validator) => {
					if (validator) validator(value, getValues())
				})
				if (isRequired && value.length === 0)
					return formatMessage({ id: 'formFields.input.isRequired', defaultMessage: 'This field is required' })

				return undefined
			},
		},
		defaultValue,
	})
	const inputRef = useRef<HTMLInputElement>(null)
	const [inputValue, setInputValue] = useState<string>('')
	const hasError = Boolean(error)
	const hasContent = Boolean(value || inputValue)

	// Options may be defined while loading, if the resource is reloading
	const { options, areOptionsLoading, filteredOptions, filteredValues } = useAsyncOptions(
		providedOptions,
		name,
		inputValue,
	)

	// Downshift setup and settings
	const {
		isOpen: isMenuOpen,
		getLabelProps,
		getMenuProps,
		highlightedIndex,
		getItemProps,
		getInputProps,
		openMenu: onOpenMenu,
		closeMenu: onCloseMenu,
		selectItem,
	} = useCombobox({
		id,
		items: filteredValues,
		itemToString: () => '',
		onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem),
		onIsOpenChange: ({ isOpen }) => {
			// If the user clears the field and exits the menu, we assume the user wants to reset the field
			if (!isOpen) setInputValue('')
		},
		onInputValueChange: ({ inputValue }) => setInputValue(inputValue ?? ''),
		initialSelectedItem: value,
		defaultSelectedItem: '',
	})
	const selectedOption = options?.find((option) => option.value === value)

	if (options && value && !selectedOption) {
		throw new Error(`Selected value ${value} has no corresponding option for field ${name}`)
	}

	// Helper hooks for better and more reliable access to hover and focus states
	const { hoverProps, isHovered } = useHover({})
	const { focusProps, isFocused } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })

	return (
		<ColumnLabel {...rest}>
			<Label
				{...getLabelProps()}
				htmlFor={id}
				id={`${id}-label`}
				hasError={hasError}
				hasContent={hasContent}
				isFocused={isFocused && !isReadOnly && !isDisabled}
				isDisabled={isDisabled}
				aria-hidden
			>
				<span>{label}</span>
			</Label>
			<InputContainer
				{...hoverProps}
				hasError={hasError}
				isDisabled={isDisabled || isReadOnly}
				isHovered={isHovered}
				isFocused={isFocused}
				isReadOnly={false}
				onClick={(event) => {
					if (event.defaultPrevented || isDisabled || isReadOnly) return undefined
					onOpenMenu()
				}}
			>
				<ValueWrapper>
					<InputComponent
						{...(getInputProps({
							ref: inputRef,
							name,
							placeholder,
							onFocus: onOpenMenu,
							onClick: onOpenMenu,
						}) as Record<string, unknown>)}
						{...focusProps}
						hasError={hasError}
						value={inputValue}
						shouldShrink={(!inputValue && Boolean(selectedOption)) || areOptionsLoading}
						shouldFill={(!inputValue && !selectedOption) || areOptionsLoading}
						id={id}
						autoFocus={shouldAutofocus}
						onFocus={(event) => {
							event.target.select()
							onOpenMenu()
							if (focusProps.onFocus) focusProps.onFocus(event)
						}}
						onBlur={async (event) => {
							onCloseMenu()
							if (focusProps.onBlur) focusProps.onBlur(event)
						}}
					/>
					{areOptionsLoading ? (
						<LoadingLabel>Loading...</LoadingLabel>
					) : (
						<InlineValue
							isVisible={!inputValue && Boolean(selectedOption)}
							label={selectedOption?.render ? selectedOption.render(selectedOption) : selectedOption?.label}
						/>
					)}
				</ValueWrapper>
				<MenuIndicator isMenuOpen={isMenuOpen} isLoading={isLoading || areOptionsLoading} hasError={hasError} />
			</InputContainer>
			<SelectMenu
				{...(getMenuProps({}, { suppressRefError: true }) as Record<string, unknown>)}
				getItemProps={getItemProps}
				highlightedOptionIndex={highlightedIndex}
				isLoading={isLoading || areOptionsLoading}
				isOpen={isMenuOpen}
				maxNumberOfOptionsVisible={maxNumberOfOptionsVisible}
				options={filteredOptions}
				selectedValues={value === undefined ? [] : [value]}
				onSelectOption={(item) => selectItem(item)}
			/>
			<InputError id={id} hasError={hasError} errorMessage={error?.message as string | undefined} />
		</ColumnLabel>
	)
}
